import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ToolCallList } from './ToolCallList';
import { useMessageTrace } from '@/hooks/useChatThreads';
import type { Message } from '@/types/chat';

interface InspectorDrawerProps {
  open: boolean;
  onClose: () => void;
  threadId: string;
  messageId: string | null;
  message: Message | null;
}

interface JsonBlockProps {
  title: string;
  data: unknown;
  defaultOpen?: boolean;
  fallback?: string;
}

function JsonBlock({ title, data, defaultOpen = true, fallback = '—' }: JsonBlockProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const [wrap, setWrap] = useState(true);

  const text =
    data === null || data === undefined
      ? fallback
      : typeof data === 'string'
        ? data
        : JSON.stringify(data, null, 2);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — silent
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card/40">
      <header className="flex items-center justify-between px-3 py-2 border-b border-border/60">
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {title}
        </button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setWrap((v) => !v)}
            title={wrap ? 'Desativar quebra de linha' : 'Ativar quebra de linha'}
          >
            {wrap ? 'Wrap' : 'No wrap'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleCopy}
            title="Copiar"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Copiado
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 mr-1" />
                Copiar
              </>
            )}
          </Button>
        </div>
      </header>
      {isOpen && (
        <pre
          className={
            'text-xs font-mono p-3 max-h-[480px] overflow-auto leading-relaxed ' +
            (wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre')
          }
        >
          {text}
        </pre>
      )}
    </section>
  );
}

export function InspectorDrawer({
  open,
  onClose,
  threadId,
  messageId,
  message,
}: InspectorDrawerProps) {
  const isAiOutgoing = message?.direction === 'OUTGOING' && message?.role?.toUpperCase() === 'AI';
  const { data: trace, isLoading, isError, isFetching } = useMessageTrace(
    threadId,
    isAiOutgoing ? messageId : null,
  );

  const traceUnavailable =
    !isLoading && !isError && isAiOutgoing && !message?.executionTrace && !trace;

  const toolCount = trace?.tools?.length ?? 0;
  const totalDuration = trace?.tools?.reduce((sum, tool) => sum + (tool.durationMs ?? 0), 0) ?? 0;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[640px] lg:w-[880px] xl:w-[1100px] overflow-y-auto p-6"
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle>Inspector de Execução</SheetTitle>
              {message && (
                <p className="text-xs text-muted-foreground mt-1">
                  {message.direction === 'INCOMING' ? 'Mensagem do contato' : 'Resposta do agente'}{' '}
                  · {new Date(message.createdAt).toLocaleString('pt-BR')}
                  {message.correlationId && (
                    <>
                      {' · '}
                      <span className="font-mono">{message.correlationId.slice(0, 8)}</span>
                    </>
                  )}
                </p>
              )}
            </div>
            <SheetClose asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {(isLoading || isFetching) && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {!isLoading && isError && (
            <div className="rounded-lg border border-muted bg-muted/50 p-3">
              <div className="flex items-start gap-2">
                <div className="text-lg mt-0.5 animate-pulse">⏳</div>
                <div>
                  <p className="font-medium text-sm">Agregando trace de execução…</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    O trace está sendo processado. Aguarde alguns segundos.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !message && (
            <div className="rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center">
              <p className="text-sm text-muted-foreground">Nenhuma mensagem selecionada</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Clique em uma mensagem para inspecionar
              </p>
            </div>
          )}

          {!isLoading && !isError && traceUnavailable && (
            <div className="rounded-lg border border-muted bg-muted/50 p-3">
              <p className="text-sm text-muted-foreground">
                Trace indisponível (mensagem anterior ao deploy ou aggregator não rodou)
              </p>
            </div>
          )}

          {/* AI message — full trace */}
          {!isLoading && !isError && !traceUnavailable && isAiOutgoing && trace && (
            <>
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <span>⚡</span>
                  <span>
                    {toolCount} {toolCount === 1 ? 'tool' : 'tools'} · {totalDuration}ms total
                  </span>
                </div>
              </div>

              <JsonBlock
                title="1. Mensagem do usuário"
                data={trace?.input ?? message?.content}
              />

              {trace?.agentRequest && (
                <JsonBlock
                  title="2. Payload enviado ao agente (n8n)"
                  data={trace.agentRequest}
                  defaultOpen={false}
                />
              )}

              <section className="rounded-lg border border-border bg-card/40">
                <header className="flex items-center justify-between px-3 py-2 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    3. Tools chamadas ({toolCount})
                  </span>
                </header>
                <div className="p-3">
                  {toolCount === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Nenhuma tool foi chamada nesta resposta
                    </p>
                  ) : (
                    <ToolCallList tools={trace?.tools ?? []} />
                  )}
                </div>
              </section>

              {trace?.agentResponse && (
                <JsonBlock
                  title="4. Resposta bruta do agente (n8n)"
                  data={trace.agentResponse}
                  defaultOpen={false}
                />
              )}

              <JsonBlock
                title="5. Resposta salva no banco"
                data={trace?.output ?? message?.content}
              />
            </>
          )}

          {/* Non-AI message */}
          {!isLoading && !isError && !isAiOutgoing && message && (
            <>
              <JsonBlock
                title="Mensagem do usuário (salva no banco)"
                data={{
                  id: message.id,
                  direction: message.direction,
                  role: message.role,
                  createdAt: message.createdAt,
                  content: message.content,
                  correlationId: message.correlationId ?? null,
                }}
              />
              <p className="text-xs text-muted-foreground">
                O trace de execução fica disponível na resposta do agente associada (clique na bolha azul).
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
